import { createContext, useContext } from "react";
import type { User } from "firebase/auth";
import type { Timestamp } from "firebase/firestore";
import type { ProfileEdit } from "./profile";

export type UserProfile = {
  username: string;
  usernameLower: string;
  usernameChangedAt: Timestamp | null;
  displayName: string | null;
  photoURL: string | null;
  email: string | null;
  bio: string | null;
  accentColor: string | null;
  createdAt: Timestamp | null;
};

export type AuthContextValue = {
  user: User | null;
  loading: boolean;
  configured: boolean;
  /** Full users/{uid} profile doc, null while loading or before claim. */
  profile: UserProfile | null;
  /** Convenience: resolved username (mixed-case display form) once claimed. */
  username: string | null;
  /** True when a signed-in user has not yet claimed a username. */
  needsUsername: boolean;
  signInWithGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
  sendEmailSignInLink: (email: string) => Promise<void>;
  claimUsername: (name: string) => Promise<void>;
  changeUsername: (newName: string) => Promise<void>;
  updateProfile: (edit: ProfileEdit) => Promise<void>;
};

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined
);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
